<template>
  <div style="margin-bottom: 3rem;">
    <div style="margin: 0 0 1rem 0;" class="chart-section-header">
      Engagements
    </div>

    <div class="engagements-period">
      <b-field label="Time Period">
        <b-datepicker
          range
          :min-date="minDate"
          :max-date="maxDate"
          v-model="selectedDates"
          placeholder="Click to select..."
        >
        </b-datepicker>

        <b-button style="display: none" type="is-primary">Update</b-button>
      </b-field>
    </div>

    <div class="engagements-summary">
      <div class="card summary-card">
        <h6>Active Users</h6>
        <h2>{{ totalNumActiveLearnersBAN }}</h2>
      </div>

      <div class="card summary-card">
        <h6>Time on App (Hrs)</h6>
        <h2>{{ (totalLearnerAppTimeBAN / 3600).toFixed(2) }}</h2>
      </div>

      <div class="card summary-card">
        <h6>App Time / user (Min)</h6>
        <h2>{{ (averageLearnerAppTimeBAN / 60).toFixed(2) }}</h2>
      </div>

      <div class="card summary-card">
        <h6>Learning Time on app (Hrs)</h6>
        <h2>{{ (totalLearningTimeBAN / 3600).toFixed(2) }}</h2>
      </div>

      <div class="card summary-card">
        <h6>Learning Time / user (Min)</h6>
        <h2>{{ (averageLearningTimeBAN / 60).toFixed(2) }}</h2>
      </div>
    </div>

    <div class="engagements-chart">
      <engagements-chart
        :chartData="chartData"
        :options="options"
      ></engagements-chart>
    </div>

    <div class="engagements-table">
      <engagements-table
        :learnerDetailsData="learnersData"
        :learnerEngagementsData="filteredEngagementsData"
      ></engagements-table>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import lodash from "lodash";
import EngagementsChart from "./EngagementsChart.vue";
import EngagementsTable from "./EngagementsTable.vue";
import { mapState, mapGetters } from "vuex";
export default {
  name: "EnagagementsWindow",
  props: {
    learnersData: Array
  },
  data() {
    const today = new Date();

    return {
      selectedDates: [
        new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30),
        new Date()
      ],
      totalActiveUsers: 0,
      totalActivityTime: 0,
      averageUserActivityTime: 0,

      engagementPeriod: "Week",
      engagementsLaunchDate: "2022-05-02", // actual launch date

      maxDate: new Date(),
      minDate: new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - 30
      ),

      baseChartOptions: {
        maintainAspectRatio: false,
        legend: {
          display: false
        },
        responsive: true
      }
    };
  },
  methods: {
    getDates(startDate, endDate) {
      const dates = [];
      let currentDate = startDate;
      const addDays = function(days) {
        const date = new Date(this.valueOf());
        date.setDate(date.getDate() + days);
        return date;
      };
      while (currentDate <= endDate) {
        dates.push(currentDate);
        currentDate = addDays.call(currentDate, 1);
      }
      return dates.map(date =>
        moment(date)
          .utc()
          .format("YYYY-MM-DD")
      );
    }
  },
  computed: {
    ...mapState({
      grade: state => state.globalStore.activeFilterParams.grade,
      class: state => state.globalStore.activeFilterParams.class,
      school: state => state.globalStore.activeFilterParams.school,
      project: state => state.globalStore.activeFilterParams.projectName
    }),
    ...mapGetters({
      rawEngagementsData: "newEngagementsStore/getRawEngagementsData"
    }),
    getXaxisValues() {
      return this.getDates(
        this.selectedDates[0],
        this.selectedDates[1]
      ).map(date => moment(date).format("MMM Do YY"));
    },
    getYaxisAppTimeValues() {
      return this.getDates(this.selectedDates[0], this.selectedDates[1]).map(
        date => {
          // calculate duration by day for all learners
          let foo = this.filteredEngagementsData.map(obj => {
            const dayObj = obj.activeDates.find(
              activeDate => activeDate.eventDate.value == date
            );
            if (dayObj) return dayObj.dailyAppTime;
            return 0;
          });
          return (lodash.sum(foo) / 3600).toFixed(2);
        }
      );
    },
    getYaxisLearningTimeValues() {
      return this.getDates(this.selectedDates[0], this.selectedDates[1]).map(
        date => {
          // calculate duration by day for all learners
          let foo = this.filteredEngagementsData.map(obj => {
            const dayObj = obj.activeDates.find(
              activeDate => activeDate.eventDate.value == date
            );
            if (dayObj) return dayObj.dailyDuration;
            return 0;
          });
          return (lodash.sum(foo) / 3600).toFixed(2);
        }
      );
    },
    chartData() {
      return {
        labels: this.getXaxisValues,
        datasets: [
          {
            label: "Total App Time",
            backgroundColor: "#AA7DFB",
            data: this.getYaxisAppTimeValues
          },
          {
            label: "Total Learning Time",
            backgroundColor: "#30866e",
            data: this.getYaxisLearningTimeValues
          }
        ]
      };
    },
    options() {
      return {
        maintainAspectRatio: false,
        legend: { display: true },
        responsive: true,
        scales: {
          yAxes: [
            {
              stacked: false,
              gridLines: {
                drawBorder: true,
                color: "rgba(29,140,248,0.0)"
              },
              scaleLabel: {
                display: true,
                labelString: "Time Spent (hours)",
                fontStyle: "bold",
                fontSize: "16"
              },
              ticks: {
                display: true,
                beginAtZero: true,
                min: 0,
                fontColor: "#9a9a9a",
                callback: function(value) {
                  if (Number.isInteger(value)) {
                    return `${value}`;
                  }
                }
              }
            }
          ],
          xAxes: [
            {
              stacked: false,
              gridLines: {
                drawBorder: true,
                color: "rgba(225,78,202,0.1)"
              },
              scaleLabel: {
                display: true,
                labelString: "Day",
                fontStyle: "bold",
                fontSize: "16"
              },
              ticks: {
                display: true,
                fontColor: "#9a9a9a"
              }
            }
          ]
        }
      };
    },
    classLearners() {
      return this.learnersData.filter(
        learnerObj => learnerObj.grade == this.grade
      );
    },
    filteredEngagementsData() {
      return this.rawEngagementsData.filter(arr =>
        this.classLearners.some(learner => learner._id == arr.uid)
      );
    },
    totalNumActiveLearnersBAN() {
      return this.filteredEngagementsData.length;
    },
    totalLearnerAppTimeBAN() {
      return lodash.sum(
        this.filteredEngagementsData.map(obj => obj.totalAppTime)
      );
    },
    averageLearnerAppTimeBAN() {
      if (this.totalNumActiveLearnersBAN <= 0) return 0;
      return this.totalLearnerAppTimeBAN / this.totalNumActiveLearnersBAN;
    },
    totalLearningTimeBAN() {
      return lodash.sum(
        this.filteredEngagementsData.map(obj => obj.totalDuration)
      );
    },
    averageLearningTimeBAN() {
      if (this.totalNumActiveLearnersBAN <= 0) return 0;
      return this.totalLearningTimeBAN / this.totalNumActiveLearnersBAN;
    }
  },
  components: {
    "engagements-chart": EngagementsChart,
    "engagements-table": EngagementsTable
  },
  mounted() {
    // console.log("Raw Engagements: ", this.rawEngagementsData);
    // console.log("Filtered Engagements: ", this.filteredEngagementsData);
    // console.log("totalNumActiveLearnersBAN: ", this.totalNumActiveLearnersBAN);
    // console.log("totalLearnerAppTimeBAN: ", this.totalLearnerAppTimeBAN);
  }
};
</script>

<style lang="scss">
.engagements-summary {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;

  margin: 1.5rem 0 3.5rem 0;

  .summary-card {
    width: 25%;
    margin-right: 1rem;

    padding: 1.5rem;

    h6 {
      color: #c1c1c1;
      font-weight: 600;
      font-size: 0.8rem;
      margin-bottom: 0.5rem;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }

    h2 {
      margin: 0;
      font-weight: 600;
      color: #4b4b4b;
    }
  }
}

.engagements-period {
  margin: 0 0 2.5rem 0;

  .field {
    width: 100%;
    align-items: center;
    justify-content: flex-end !important;

    label {
      margin: 0 1rem 0 0;
    }

    .datepicker {
      width: 20%;
      margin: 0 1rem 0 0 !important;

      input {
        border: 1px #c1c1c1 solid;
      }
    }
  }
}

.engagements-chart {
  margin: 0 0 3rem 0;
}

.engagements-table {
  margin: 0 0 3rem 0;
}
</style>
